import { captureException } from '@sentry/browser';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { colors } from '@modernhealth/vital-foundation';
import {
    BodyTextSmall,
    ComponentTextMedium,
    ComponentTextSmall,
    Icon,
    pxToRem,
} from '@modernhealth/vital-components';
import { useAnalytics } from '@modernhealth/web-analytics';
import { Menu, MenuList as ML, MenuButton as MB, MenuItem as MI } from '@reach/menu-button';
import styled from '@emotion/styled';

import { navigationService } from 'src/config/rootService';
import { getFullName } from 'src/modules/user/selectors/userSelectors';
import { config } from 'src/modules/admin/config/config';
import { ReactComponent as UserIcon } from 'src/public/img/icon-user-blue.svg';
import { ReactComponent as ToolIcon } from 'src/public/img/icon-wrench-tool.svg';
import { ReactComponent as OpenBookIcon } from 'src/public/img/icon-open-book.svg';
import * as S from 'src/shared/layout/styles';

interface MenuButtonProps {
    isOnMainAdminPage?: boolean;
}

const MenuButton = styled(MB)(({ isOnMainAdminPage }: MenuButtonProps) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: pxToRem(16),
    border: 'none',
    position: 'relative',
    top: 0,
    left: 0,
    backgroundColor: isOnMainAdminPage ? colors.blue05 : colors.white,
    '&:hover': {
        cursor: 'pointer',
        borderRadius: pxToRem(8),
    },
}));

const MenuButtonLabel = styled(ComponentTextMedium)({
    margin: pxToRem(0, 8),
});

const MenuList = styled(ML)({
    display: 'flex',
    flexDirection: 'column',
    minWidth: pxToRem(304),
    border: 'none',
    borderRadius: pxToRem(12),
    padding: pxToRem(16),
    backgroundColor: colors.white,
    boxShadow: '0px 0px 8px rgba(33, 38, 46, 0.12), 0px 4px 12px rgba(33, 38, 46, 0.16)',
    position: 'absolute',
});

const MenuItem = styled(MI)({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'start',
    alignItems: 'flex-start',
    gap: pxToRem(4),
    padding: pxToRem(16),
    height: '100%',
    userSelect: 'text',
    '&:hover': {
        backgroundColor: colors.blue05,
        cursor: 'pointer',
        borderRadius: pxToRem(8),
        userSelect: 'text',
    },
});

const Label = styled(ComponentTextMedium)({
    borderRadius: pxToRem(12),
});

const Description = styled(BodyTextSmall)({
    borderRadius: pxToRem(12),
    padding: pxToRem(4, 0),
});

const AdminBadge = styled(ComponentTextSmall)({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: pxToRem(5),
    width: pxToRem(104),
    maxHeight: pxToRem(21),
    backgroundColor: colors.blue10,
    textAlign: 'center',
    color: colors.blue55,
    margin: pxToRem(0, 16),
    padding: pxToRem(4, 0),
    '&:hover': {
        color: colors.blue55,
    },
});

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
});

const StyledUserIcon = styled(UserIcon)({
    color: colors.blue55,
});

const StyledOpenBookIcon = styled(OpenBookIcon)({
    color: colors.blue55,
});

const StyledToolIcon = styled(ToolIcon)({
    color: colors.blue55,
});

interface AdminMenuProps {
    isOnMainAdminPage?: boolean;
}

interface Props {
    onLogOut: () => void;
}

export const AdminProfileMenu: React.FC<Props & AdminMenuProps> = ({
    onLogOut,
    isOnMainAdminPage,
}) => {
    const { t } = useTranslation();
    const fullName = useSelector(getFullName);

    return (
        <Wrapper>
            <AdminBadge>{t('admin:admin_view')}</AdminBadge>
            <Menu>
                <MenuButton isOnMainAdminPage={isOnMainAdminPage}>
                    <StyledUserIcon />
                    <S.UserName>{fullName}</S.UserName>
                    <Icon name="triangle-down" />
                </MenuButton>
                <MenuList>
                    <MenuItem
                        onSelect={() => {
                            navigationService.navigate('/dashboard');
                        }}
                    >
                        <Description bold>{t('admin:switch_to_member_view')}</Description>
                    </MenuItem>
                    <MenuItem onSelect={() => onLogOut()}>
                        <Description bold>{t('shared:header.log_out')}</Description>
                    </MenuItem>
                </MenuList>
            </Menu>
        </Wrapper>
    );
};

export const AdminResourcesMenu: React.FC<AdminMenuProps> = ({ isOnMainAdminPage }) => {
    const { t } = useTranslation();
    const analytics = useAnalytics();
    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(config.clientPortalPassword);
        } catch (err) {
            captureException(err);
        }
    };

    const handleOpenClientPortal = async () => {
        await copyToClipboard();
        analytics.trackClick('admin portal navigation bar', {
            translation_key: 'admin:menu.resources.button.title',
            destination: config.clientPortalUrl,
            page_section: 'navigation bar',
        });
        window.open(config.clientPortalUrl, '_blank');
    };

    return (
        <Wrapper>
            <Menu>
                <MenuButton isOnMainAdminPage={isOnMainAdminPage}>
                    <StyledOpenBookIcon />
                    <MenuButtonLabel>{t('admin:menu.resources.button.title')}</MenuButtonLabel>
                    <Icon name="triangle-down" />
                </MenuButton>
                <MenuList>
                    <MenuItem onSelect={handleOpenClientPortal}>
                        <Label bold>
                            {t('admin:menu.resources.menu_item.engagement_resources.label')}
                        </Label>
                        <Description css={{ width: '90%', padding: 0 }}>
                            {t('admin:menu.resources.menu_item.engagement_resources.description', {
                                password: config.clientPortalPassword,
                            })}
                        </Description>
                        <Description css={{ width: '100%', padding: 0 }}>
                            {t('admin:menu.resources.menu_item.engagement_resources.hint')}
                        </Description>
                    </MenuItem>
                </MenuList>
            </Menu>
        </Wrapper>
    );
};

export const AdminToolsMenu: React.FC<AdminMenuProps> = ({ isOnMainAdminPage }) => {
    const { t } = useTranslation();
    const analytics = useAnalytics();
    const { showHrisConnectButton } = useFlags();

    const handleOnClick = (path: string, key: string) => {
        analytics.trackClick('admin portal navigation bar', {
            translation_key: key,
            destination: path,
            page_section: 'navigation bar',
        });
        navigationService.navigate(path);
    };

    return (
        <Wrapper>
            <Menu>
                <MenuButton isOnMainAdminPage={isOnMainAdminPage}>
                    <StyledToolIcon color={colors.blue55} aria-label="tools icon" />
                    <MenuButtonLabel>{t('admin:menu.tools.button.title')}</MenuButtonLabel>
                    <Icon name="triangle-down" />
                </MenuButton>
                <MenuList>
                    <MenuItem
                        onSelect={() => {
                            handleOnClick(
                                '/admin/benefits',
                                'admin:menu.tools.menu_item.manage_benefits.label',
                            );
                        }}
                    >
                        <Label bold>{t('admin:menu.tools.menu_item.manage_benefits.label')}</Label>
                        <Description>
                            {t('admin:menu.tools.menu_item.manage_benefits.description')}
                        </Description>
                    </MenuItem>
                    <MenuItem
                        onSelect={() => {
                            handleOnClick(
                                '/admin/manage',
                                'admin:menu.tools.menu_item.manage_admin.label',
                            );
                        }}
                    >
                        <Label bold>{t('admin:menu.tools.menu_item.manage_admin.label')}</Label>
                        <Description>
                            {t('admin:menu.tools.menu_item.manage_admin.description')}
                        </Description>
                    </MenuItem>
                    {showHrisConnectButton && (
                        <MenuItem
                            onSelect={() => {
                                handleOnClick(
                                    '/admin/hris-sync',
                                    'admin:menu.tools.menu_item.hris_sync.label',
                                );
                            }}
                        >
                            <Label bold>{t('admin:menu.tools.menu_item.hris_sync.label')}</Label>
                            <Description>
                                {t('admin:menu.tools.menu_item.hris_sync.description')}
                            </Description>
                        </MenuItem>
                    )}
                </MenuList>
            </Menu>
        </Wrapper>
    );
};
